import axios from 'axios'
import sha256 from 'js-sha256'
import { v4 as uuidv4 } from 'uuid'
import { handleNetworkError } from '../utils/errorHandler'

export const APPID = 'web.lingxi.voice';
export const SECRET_KEY = '';

export function getDeviceId() {
  let id = localStorage.getItem('device_id');
  if (!id) {
    const raw = uuidv4().replace(/-/g, ''); // 32 个十六进制字符
    id = 'web-' + raw.substring(0, 27);     // 保证总长为 32 个字符    
    localStorage.setItem('device_id', id);
  }
  return id;
}

export function genSignature(appid, device_id, timestamp, secret_key, uri) {
  return sha256(appid + device_id + timestamp + secret_key + uri);
}

// 配置axios
const isDev = process.env.NODE_ENV === 'development';

// 如果是开发环境，用代理前缀，比如 /api
// 如果是生产环境，默认就是当前域名
const baseURL = isDev ? '' : `${window.location.protocol}//${window.location.hostname}`;

const request = axios.create({
    baseURL: baseURL,
    timeout: 120000
});

// token 刷新锁
let isRefreshing = false;
let refreshPromise = null;
let refreshRetryCount = 0; // 新增重试计数
const MAX_REFRESH_RETRY = 3; // 最大重试次数

// 请求拦截器
request.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        // 公共头
        const appid = APPID;
        const device_id = getDeviceId();
        const timestamp = Math.floor(Date.now() / 1000);
        // 需要根据实际接口路径生成 signature
        let uri = config.url;
        if (uri.startsWith('http')) {
          try {
            uri = new URL(uri).pathname;
          } catch (e) {    
            console.error('Invalid URL in signature generation:', e);
          }
        }
        const signature = genSignature(appid, device_id, timestamp, SECRET_KEY, uri);
        
        // 确保不覆盖 Content-Type
        if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }
        
        // 添加其他公共头
        config.headers['appid'] = appid;
        config.headers['device_id'] = device_id;
        config.headers['timestamp'] = timestamp;
        config.headers['signature'] = signature;
        
        return config;
    },
    error => Promise.reject(error)
);

// 响应拦截器
async function handleTokenExpired(config) {
    if (refreshRetryCount >= MAX_REFRESH_RETRY) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject('登录已过期，请重新登录');
    }
    if (!isRefreshing) {
        isRefreshing = true;
        const refresh_token = localStorage.getItem('refresh_token');
        if (refresh_token) {
            refreshPromise = request.post('/admin/auth/refresh-token', { refresh_token })
                .then(res => {
                    if (res.code === 0) {
                        localStorage.setItem('token', res.data.token);
                        localStorage.setItem('refresh_token', res.data.refresh_token);
                        isRefreshing = false;
                        refreshRetryCount = 0; // 刷新成功重置计数
                        return Promise.resolve(res);
                    } else {
                        refreshRetryCount++;
                        if (refreshRetryCount >= MAX_REFRESH_RETRY) {
                            localStorage.removeItem('token');
                            localStorage.removeItem('refresh_token');
                            window.location.href = '/login';
                            return Promise.reject('登录已过期，请重新登录');
                        }
                        return Promise.reject('登录已过期，请重新登录');
                    }
                })
                .catch(err => {
                    refreshRetryCount++;
                    if (refreshRetryCount >= MAX_REFRESH_RETRY) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('refresh_token');
                        window.location.href = '/login';
                    }
                    throw err;
                })
                .finally(() => { isRefreshing = false; });
        } else {
            window.location.href = '/login';
            return Promise.reject('未登录');
        }
    } else {
        refreshRetryCount++;
    }
    await refreshPromise;
    config.headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
    return request(config);
}

request.interceptors.response.use(
    async response => {
        if (response && response.data && response.data.code === 401) {
            return handleTokenExpired(response.config);
        }
        return response.data;
    },
    async error => {
        // 处理网络错误和服务器错误
        if (error.response) {
            // 服务器返回了错误状态码
            if (error.response.status === 401) {
                return handleTokenExpired(error.config);
            }
            // 其他错误状态码，返回错误信息
            return Promise.reject({
                code: error.response.status,
                message: error.response.data?.message || '请求失败',
                data: null
            });
        } else if (error.request) {
            // 请求已发出但没有收到响应
            return Promise.reject({
                code: 0,
                message: '网络连接失败，请检查网络设置',
                data: null
            });
        } else {
            // 使用统一的网络错误处理
            const networkError = handleNetworkError(error);
            if (networkError) {
                return Promise.reject(networkError);
            }
            
            // 请求配置出错
            return Promise.reject({
                code: 0,
                message: error.message || '请求配置错误',
                data: null
            });
        }
    }
);

export default request 